<template>
  <div v-if="show" class="item-precario-data-table w-full h-full">
    <!-- Import/Export Toolbar -->
    <div class="import-export-toolbar bg-[var(--fundoForm)] p-3 rounded-lg mb-4 flex justify-end items-center gap-2">
      <button
        @click="exportTemplate"
        :disabled="isExporting"
        class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2 text-sm"
        title="Exportar modelo Excel com dados atuais"
      >
        <i v-if="isExporting" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-file-export"></i>
        Exportar Modelo
      </button>
      <label
        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center gap-2 text-sm cursor-pointer"
        :class="{ 'opacity-50 pointer-events-none': isImporting }"
        title="Importar preços a partir de Excel"
      >
        <i v-if="isImporting" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-file-import"></i>
        Importar Preços
        <input
          type="file"
          accept=".xlsx"
          class="hidden"
          ref="importFileInput"
          @change="handleImportFile"
          :disabled="isImporting"
        />
      </label>
    </div>

    <!-- Bulk Actions Toolbar -->
    <div v-if="selectedItems.length > 0" class="bulk-actions-toolbar bg-[var(--fundoForm)] p-3 rounded-lg mb-4 flex justify-between items-center">
      <div class="flex items-center gap-4">
        <span class="text-[var(--text)]">{{ selectedItems.length }} itens selecionados</span>
        <button
          @click="clearSelection"
          class="bg-gray-500 hover:bg-gray-600 text-[var(--text)] px-3 py-1 rounded text-sm"
        >
          Limpar Seleção
        </button>
      </div>
      <div class="flex gap-2">
        <button
          @click="handleBulkEdit"
          class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2"
        >
          <i class="fas fa-edit"></i>
          Edição em Lote
        </button>
      </div>
    </div>

    <BackofficeDataTable
      ref="dataTable"
      v-if="show"
      :show="show"
      :visibility="visibility"
      :table_title="tableTitle"
      :api-url="apiUrl"
      :columns="columns"
      :available-filters="availableFilters"
      :initial-params="initialParams"
      :show_export="showExport"
      :show_filters="showFilters"
      :show_actions="showActions"
      :show_close_btn="showCloseBtn"
      :selected-row-id="selectedRowId"
      :enable-bulk-selection="true"
      :selected-items="selectedItems"
      @row-click="handleRowClick"
      @edit="handleEdit"
      @delete="handleDelete"
      @export="handleExport"
      @filters-changed="handleFiltersChanged"
      @close="handleClose"
      @data-loaded="handleDataLoaded"
      @selection-changed="handleSelectionChanged"
    >

      <!-- Custom column for status -->
      <template #column-status="{ item }">
        <span
          :class="{
            'text-green-400': item.status === 'ativo',
            'text-red-400': item.status === 'inativo',
            'text-gray-400': item.status === 'eliminado'
          }"
          class="px-2 py-1 rounded-full text-xs font-semibold uppercase"
        >
          {{ item.status || 'N/A' }}
        </span>
      </template>

      <!-- Custom column for prices -->
      <template #column-venda="{ value }">
        <span class="font-semibold text-green-400">
          {{ formatCurrency(value) }}
        </span>
      </template>

      <template #column-compraAtual="{ value }">
        <span class="text-blue-400">
          {{ formatCurrency(value) }}
        </span>
      </template>

      <template #column-vendaMin="{ value }">
        <span class="text-yellow-400">
          {{ formatCurrency(value) }}
        </span>
      </template>

      <!-- Custom actions -->
      <template #actions="{ item }">
        <button
          @click.stop="handleEdit(item)"
          class="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded mr-1 text-xs"
          title="Editar"
        >
          <i class="fas fa-edit"></i>
        </button>
      </template>
    </BackofficeDataTable>
  </div>
</template>

<script>
import axios from 'axios'
import jszip from 'jszip'
import BackofficeDataTable from '@/components/dataTable/BackofficeDataTable.vue'

export default {
  name: 'ItemPrecarioDataTable',
  components: {
    BackofficeDataTable
  },
  props: {
    // Display props
    show: {
      type: Boolean,
      default: false
    },
    visibility: {
      type: Boolean,
      default: true
    },
    tableTitle: {
      type: String,
      default: 'Preços Equipamentos'
    },
    searchBy: {
      type: String,
      default: 'Equipamentos'
    },
    
    // Feature toggles
    showExport: {
      type: Boolean,
      default: true
    },
    showFilters: {
      type: Boolean,
      default: true
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showCloseBtn: {
      type: Boolean,
      default: true
    },
    
    // Data configuration
    tabelaPreco: {
      type: Object,
      default: () => ({})
    }
  },
  
  data() {
    return {
      baseURl: process.env.VUE_APP_URL_BASE,
      
      // Table configuration
      columns: [
        { key: 'equipamento', label: 'Equipamento', type: 'text' },
        { key: 'venda', label: 'Venda', type: 'currency' },
        { key: 'vendaMin', label: 'Venda Mín.', type: 'currency' },
        { key: 'compraAtual', label: 'Compra Atual', type: 'currency' },
      ],
      
      // Currency and selection
      selectedRowId: null,
      currentCurrency: 'EUR', // Default fallback
      currentCurrencySymbol: '€',
      
      // Import/Export state
      isExporting: false,
      isImporting: false,
      
      // Bulk selection
      selectedItems: [],
      tableData: [], // Store current table data for bulk operations
      
      availableFilters: [
        // Hierarchical filters - ORDER MATTERS for cascading
        {
          key: 'equipamento_familia_id',
          label: '1. Família',
          type: 'select',
          options: [],
          dependsOn: null
        },
        {
          key: 'equipamento_produto_id',
          label: '2. Produto',
          type: 'select',
          options: [],
          dependsOn: 'equipamento_familia_id'
        },
        {
          key: 'equipamento_tipologia_id',
          label: '3. Tipologia',
          type: 'select',
          options: [],
          dependsOn: ['equipamento_familia_id', 'equipamento_produto_id']
        },
        {
          key: 'equipamento_tpServico_id',
          label: 'Tipo de Serviço',
          type: 'select',
          options: [],
          dependsOn: null
        },
        {
          key: 'equipamento_tpMedida_id',
          label: 'Tipo de Medida',
          type: 'select',
          options: [],
          dependsOn: null
        },
        {
          key: 'equipamento_tpMarca_id',
          label: 'Marca',
          type: 'select',
          options: [],
          dependsOn: null
        }
      ],
      
      // Store all data for cascading filters
      allFamilias: [],
      allProdutos: [],
      allTipologias: [],
      allTpServicos: [],
      allTpMedidas: [],
      allTpMarcas: []
    }
  },
  
  computed: {
    apiUrl() {
      return `${this.baseURl}/api/v1/itemprecario/pagination/`
    },
    
    initialParams() {
      const params = {
        l: 1 // Default local_id
      }
      
      if (this.tabelaPreco && this.tabelaPreco.id) {
        params.campo = 'precario_id'
        params.valor = this.tabelaPreco.id
      }
      
      return params
    }
  },
  
  mounted() {
    this.loadFilterOptions()
    this.loadCurrencyInfo()
  },
  
  watch: {
    tabelaPreco: {
      handler() {
        // Refresh table when tabelaPreco changes and reload currency info
        this.loadCurrencyInfo()
        this.$nextTick(() => {
          if (this.$refs.dataTable) {
            this.$refs.dataTable.refresh()
          }
        })
      },
      deep: true
    }
  },
  
  methods: {
    // Event handlers - emit to parent
    handleRowClick(row) {
      this.selectedRowId = row.id
      this.$emit('row-click', row)
    },
    
    handleEdit(item) {
      this.$emit('edit', item)
    },
    
    handleDelete(item) {
      this.$emit('delete', item)
    },
    
    async handleExport(data) {
      const format = data.format // 'pdf', 'csv', or 'xlsx'

      if (!format) {
        console.error('Export format not specified')
        return
      }

      try {
        // Build params from current filters and search
        const params = {
          export: format === 'xlsx' ? 'excel' : format, // Backend expects 'excel' not 'xlsx'
          ...this.initialParams
        }

        // Get current filters from BackofficeDataTable if available
        if (this.$refs.dataTable) {
          const currentFilters = this.$refs.dataTable.activeFilters || {}
          const searchQuery = this.$refs.dataTable.searchQuery || ''
          const sortField = this.$refs.dataTable.sortField
          const sortOrder = this.$refs.dataTable.sortOrder

          // Add filters with 'filter_' prefix
          Object.entries(currentFilters).forEach(([key, value]) => {
            if (value) {
              params[`filter_${key}`] = value
            }
          })

          // Add search query
          if (searchQuery) {
            params.search = searchQuery
          }

          // Add sorting
          if (sortField) {
            params.order_by = sortOrder === 'desc' ? `-${sortField}` : sortField
          }
        }

        // Make API request to download file
        const response = await axios.get(this.apiUrl, {
          params,
          responseType: 'blob' // Important for file download
        })

        // Create download link
        const blob = new Blob([response.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url

        // Set filename based on format
        const timestamp = new Date().toISOString().split('T')[0]
        const tabelaNome = this.tabelaPreco?.nome || 'TabelaPreco'
        const fileExtensions = {
          pdf: 'pdf',
          csv: 'csv',
          excel: 'xlsx'
        }
        const ext = fileExtensions[format === 'xlsx' ? 'excel' : format] || format
        link.download = `${tabelaNome}_${timestamp}.${ext}`

        // Trigger download
        document.body.appendChild(link)
        link.click()

        // Cleanup
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        // Also emit to parent in case they want to handle it
        // this.$emit('export', data)

      } catch (error) {
        console.error('Error exporting data:', error)
        alert('Erro ao exportar dados. Por favor, tente novamente.')
      }
    },
    
    handleClose() {
      this.$emit('close')
    },

    // Handle data loaded from BackofficeDataTable
    handleDataLoaded(data) {
      this.tableData = data || []
      // Clear selection when data changes
      this.selectedItems = []
      this.emitSelectionChange()
    },

    // Selection event handlers (simplified since BackofficeDataTable handles the logic)
    handleSelectionChanged(selectedItems) {
      this.selectedItems = selectedItems
      this.$emit('selection-changed', selectedItems)
    },

    handleBulkEdit() {
      if (this.selectedItems.length > 0) {
        this.$emit('bulk-edit', this.selectedItems)
      }
    },
    
    // Utility methods
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') {
        return `${this.currentCurrencySymbol}0,00`
      }
      
      try {
        // Use EUR formatting for all currencies to avoid crashes
        const formatted = new Intl.NumberFormat('pt-PT', {
          style: 'currency',
          currency: 'EUR',
          currencyDisplay: 'symbol'
        }).format(value)
        
        // Replace € symbol with the correct currency symbol
        return formatted.replace('€', this.currentCurrencySymbol)
      } catch (error) {
        // Fallback if formatting fails
        return `${this.currentCurrencySymbol}${value || 0}`
      }
    },
    
    // Load currency information from tabelaPreco
    async loadCurrencyInfo() {
      if (this.tabelaPreco && this.tabelaPreco.tpMoeda_id) {
        try {
          const res = await axios.get(`${this.baseURl}/api/v1/tpmoeda/${this.tabelaPreco.tpMoeda_id}/`)
          if (res.data && res.data.data) {
            const moeda = res.data.data
            const currencyName = moeda.nome?.toLowerCase() || 'euro'
            
            // Map your actual currencies to symbols
            const symbolMap = {
              'euro': '€',
              'dolar': '$', 
              'escudo': 'Esc',
              'escudo cabo verdiano': 'CVE'
            }
            
            this.currentCurrency = currencyName
            this.currentCurrencySymbol = symbolMap[currencyName] || '€'
          }
        } catch (error) {
          console.error('Error loading currency info:', error)
          // Keep defaults
        }
      }
    },
    
    // Filter loading methods
    async loadFilterOptions() {
      try {
        // Load only essential filters first (Familia and TpServico)
        await Promise.all([
          this.loadAllFamilias(),
          this.loadAllTpServicos(),
          this.loadAllMedidas(),
          this.loadAllMarcas()
        ])
        
        // Initialize filters immediately with available data
        this.updateFamiliaOptions()
        
        // Load remaining filters in background
        Promise.all([
          this.loadAllProdutos(),
          this.loadAllTipologias()
        ]).then(() => {
          // These will be loaded when user selects familia
        })
        
      } catch (error) {
        console.error('Error loading filter options:', error)
        // Initialize with empty options if loading fails
        this.updateFamiliaOptions()
      }
    },

    // Load all data methods
    async loadAllFamilias() {
      try {
        const res = await axios.get(`${this.baseURl}/api/v1/familia/`, { timeout: 10000 })
        this.allFamilias = res.data.data || []
      } catch (error) {
        console.error('Error loading familias:', error)
        this.allFamilias = []
      }
    },

    async loadAllProdutos() {
      try {
        const res = await axios.get(`${this.baseURl}/api/v1/produto/`, { timeout: 10000 })
        this.allProdutos = res.data.data || []
      } catch (error) {
        console.error('Error loading produtos:', error)
        this.allProdutos = []
      }
    },

    async loadAllTipologias() {
      try {
        const res = await axios.get(`${this.baseURl}/api/v1/tipologia/`, { timeout: 10000 })
        this.allTipologias = res.data.data || []
      } catch (error) {
        console.error('Error loading tipologias:', error)
        this.allTipologias = []
      }
    },

    async loadAllTpServicos() {
      try {
        const res = await axios.get(`${this.baseURl}/api/v1/tpservico/`, { timeout: 10000 })
        this.allTpServicos = res.data.data || []
      } catch (error) {
        console.error('Error loading tpServicos:', error)
        this.allTpServicos = []
      }
    },

    async loadAllMedidas() {
      try {
        const res = await axios.get(`${this.baseURl}/api/v1/tpmedida/`, { timeout: 10000 })
        this.allTpMedidas = res.data.data || []
      } catch (error) {
        console.error('Error loading medidas:', error)
        this.allTpMedidas = []
      }
    },

    async loadAllMarcas() {
      try {
        const res = await axios.get(`${this.baseURl}/api/v1/tpmarca/`, { timeout: 10000 })
        this.allTpMarcas = res.data.data || []
      } catch (error) {
        console.error('Error loading marcas:', error)
        this.allTpMarcas = []
      }
    },

    // Cascading filter update methods
    updateFamiliaOptions() {
      // Create a completely new array to ensure Vue 3 reactivity
      const updatedFilters = this.availableFilters.map(filter => {
        if (filter.key === 'equipamento_familia_id') {
          const familiaOptions = this.allFamilias.map(f => ({ 
            value: f.id, 
            label: f.nome 
          }))
          return {
            ...filter,
            options: familiaOptions
          }
        }
        return filter
      })
      
      // Assign the new array to trigger reactivity
      this.availableFilters = updatedFilters
      
      // Also populate independent filters
      this.updateTpServicoOptions()
      this.updateMedidaOptions()
      this.updateMarcaOptions()
    },

    updateProdutoOptions(selectedFamiliaId) {
      this.availableFilters = this.availableFilters.map(filter => {
        if (filter.key === 'equipamento_produto_id') {
          if (selectedFamiliaId) {
            const filteredProdutos = this.allProdutos.filter(p => {
              const familiaId = p.familia?.id || p.familia_id
              return Number(familiaId) === Number(selectedFamiliaId)
            })
            
            return {
              ...filter,
              options: filteredProdutos.map(p => ({ 
                value: p.id, 
                label: p.produto || p.nome || `Produto ${p.id}`
              }))
            }
          } else {
            return {
              ...filter,
              options: []
            }
          }
        }
        return filter
      })
    },

    updateTipologiaOptions(selectedFamiliaId, selectedProdutoId) {
      this.availableFilters = this.availableFilters.map(filter => {
        if (filter.key === 'equipamento_tipologia_id') {
          if (selectedFamiliaId && selectedProdutoId) {
            const filteredTipologias = this.allTipologias.filter(t => {
              const familiaId = t.familia?.id || t.familia_id
              const produtoId = t.produto?.id || t.produto_id
              const familiaMatch = Number(familiaId) === Number(selectedFamiliaId)
              const produtoMatch = Number(produtoId) === Number(selectedProdutoId)
              return familiaMatch && produtoMatch
            })
            return {
              ...filter,
              options: filteredTipologias.map(t => ({ 
                value: t.id, 
                label: t.nome 
              }))
            }
          } else {
            return {
              ...filter,
              options: []
            }
          }
        }
        return filter
      })
    },

    updateTpServicoOptions() {
      const updatedFilters = this.availableFilters.map(filter => {
        if (filter.key === 'equipamento_tpServico_id') {
          return {
            ...filter,
            options: this.allTpServicos.map(s => ({ 
              value: s.id, 
              label: s.nome 
            }))
          }
        }
        return filter
      })
      this.availableFilters = updatedFilters
    },

    updateMedidaOptions() {
      const updatedFilters = this.availableFilters.map(filter => {
        if (filter.key === 'equipamento_tpMedida_id') {
          return {
            ...filter,
            options: this.allTpMedidas.map(m => ({ 
              value: m.id, 
              label: m.nome 
            }))
          }
        }
        return filter
      })
      this.availableFilters = updatedFilters
    },

    updateMarcaOptions() {
      const updatedFilters = this.availableFilters.map(filter => {
        if (filter.key === 'equipamento_tpMarca_id') {
          return {
            ...filter,
            options: this.allTpMarcas.map(m => ({ 
              value: m.id, 
              label: m.nome 
            }))
          }
        }
        return filter
      })
      this.availableFilters = updatedFilters
    },

    // Handle cascading filter changes
    handleFiltersChanged(filters, changedFilterKey) {
      const familiaId = filters.equipamento_familia_id
      const produtoId = filters.equipamento_produto_id
      
      // Handle cascading based on which filter changed
      if (changedFilterKey === 'equipamento_familia_id') {
        // Familia changed - update produtos and clear tipologias
        this.updateProdutoOptions(familiaId)
        this.updateTipologiaOptions(null, null)
        
      } else if (changedFilterKey === 'equipamento_produto_id') {
        // Produto changed - update tipologias
        this.updateTipologiaOptions(familiaId, produtoId)
        
      } else {
        // For initial load or other changes, update all options
        this.updateProdutoOptions(familiaId)
        this.updateTipologiaOptions(familiaId, produtoId)
      }
      this.selectedItems = []
      // Emit filter changes to parent if needed
      this.$emit('filters-changed', filters, changedFilterKey)
    },
    
    // Public methods for parent component
    refresh() {
      if (this.$refs.dataTable) {
        this.$refs.dataTable.refresh()
      }
      // Clear selection when refreshing
      this.selectedItems = []
      this.$emit('selection-changed', [])
    },
    
    clearSelection() {
      this.selectedRowId = null
      this.selectedItems = []
      this.$emit('selection-changed', [])
    },

    // ======== EXPORT TEMPLATE ========
    async exportTemplate() {
      if (!this.tabelaPreco?.id) {
        alert('Nenhuma tabela de preços selecionada.')
        return
      }
      this.isExporting = true
      try {
        // Fetch all items for this price table
        const res = await axios.get(`${this.baseURl}/api/v1/itemprecario/pagination/`, {
          params: {
            campo: 'precario_id',
            valor: this.tabelaPreco.id,
            page_size: 50000,
            page: 1,
            l: 1
          }
        })
        const allData = res.data?.results || []
        if (allData.length === 0) {
          alert('Sem dados para exportar.')
          return
        }
        this.generateTemplateXlsx(allData)
      } catch (error) {
        console.error('Erro ao exportar modelo:', error)
        alert('Erro ao exportar modelo.')
      } finally {
        this.isExporting = false
      }
    },

    generateTemplateXlsx(data) {
      const escapeXml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
      const cellRef = (col, row) => {
        let s = ''
        let c = col
        do {
          s = String.fromCharCode(65 + (c % 26)) + s
          c = Math.floor(c / 26) - 1
        } while (c >= 0)
        return s + (row + 1)
      }

      const cols = [
        { key: 'id', label: 'id' },
        { key: 'equipamento', label: 'equipamento' },
        { key: 'equipamento_id', label: 'equipamento_id' },
        { key: 'venda', label: 'venda', numeric: true },
        { key: 'vendaMin', label: 'vendaMin', numeric: true },
        { key: 'compraAtual', label: 'compraAtual', numeric: true },
        { key: 'compraMin', label: 'compraMin', numeric: true },
        { key: 'compraMed', label: 'compraMed', numeric: true },
      ]

      let sheetData = '<sheetData>'
      // Header row
      sheetData += '<row r="1">'
      cols.forEach((col, ci) => {
        const ref = cellRef(ci, 0)
        sheetData += `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(col.label)}</t></is></c>`
      })
      sheetData += '</row>'

      // Data rows
      data.forEach((row, ri) => {
        sheetData += `<row r="${ri + 2}">`
        cols.forEach((col, ci) => {
          const ref = cellRef(ci, ri + 1)
          const val = row[col.key]
          const num = parseFloat(val)
          if (col.numeric && !isNaN(num)) {
            sheetData += `<c r="${ref}"><v>${num}</v></c>`
          } else {
            sheetData += `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(val)}</t></is></c>`
          }
        })
        sheetData += '</row>'
      })
      sheetData += '</sheetData>'

      const lastRef = cellRef(cols.length - 1, data.length)

      const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <dimension ref="A1:${lastRef}"/>
  ${sheetData}
</worksheet>`

      const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets><sheet name="Tabela Precos" sheetId="1" r:id="rId1"/></sheets>
</workbook>`

      const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`

      const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`

      const topRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`

      const zip = new jszip()
      zip.file('[Content_Types].xml', contentTypesXml)
      zip.file('_rels/.rels', topRelsXml)
      zip.file('xl/workbook.xml', workbookXml)
      zip.file('xl/_rels/workbook.xml.rels', relsXml)
      zip.file('xl/worksheets/sheet1.xml', sheetXml)

      zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        .then(blob => {
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `Modelo_Tabela_Precos_${this.tabelaPreco.id}.xlsx`
          a.click()
          URL.revokeObjectURL(url)
        })
    },

    // ======== IMPORT PRICES ========
    async handleImportFile(event) {
      const file = event.target.files?.[0]
      if (!file) return

      if (!this.tabelaPreco?.id) {
        alert('Nenhuma tabela de preços selecionada.')
        this.$refs.importFileInput.value = ''
        return
      }

      this.isImporting = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('precario_id', this.tabelaPreco.id)

        const res = await axios.post(`${this.baseURl}/api/v1/itemprecario/import/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        const result = res.data?.data
        const msg = res.data?.message || 'Importação concluída.'
        const errorList = result?.errors?.length ? `\n\nErros:\n${result.errors.join('\n')}` : ''
        alert(`${msg}${errorList}`)

        // Refresh table to reflect changes
        this.refresh()
      } catch (error) {
        const errMsg = error.response?.data?.message || 'Erro ao importar ficheiro.'
        alert(errMsg)
        console.error('Erro de importação:', error)
      } finally {
        this.isImporting = false
        this.$refs.importFileInput.value = ''
      }
    }
  }
}
</script>